import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Button } from "@mui/material";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { fetchLeads } from "../LeadBoard/LeadBoard";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

const reminderFieldConfiguration = [
  {
    name: "description",
    label: "Description",
    type: "text",
    validation: {
      required: "Description is required",
      minLength: {
        value: 1,
        message: "Description must be at least 1 character long",
      },
      maxLength: {
        value: 500,
        message: "Description must not exceed 500 characters",
      },
    },
  },
  {
    name: "scheduledTime",
    label: "Scheduled Time",
    type: "date",
    validation: {
      required: "Scheduled time is required",
    },
  },
  {
    name: "leadId",
    label: "Lead",
    type: "combo",
    dataKey: "leads",
  },
];

const reminderInitialValues = {
  description: "",
  scheduledTime: "",
  isCompleted: false,
  leadId: "",
};

const ReminderPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads"],
    queryFn: () => fetchLeads(token),
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  const createReminderMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/reminder`, data, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["reminders"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to create reminder");
    },
  });

  const submitHandler = (data: any) => {
    const requiredFields = ["description", "scheduledTime", "leadId"];
    const missingFields = requiredFields.filter((field) => !data[field]);

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    const submitData = {
      ...data,
      leadId: getId(data.leadId),
    };

    createReminderMutation.mutate(submitData);
  };

  const leadsOptions = leads.map((lead: any) => ({
    label: `${lead.company} | ${lead.dot}`,
    id: lead.id.toString(),
  }));

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        sx={{
          mt: 1,
        }}
      >
        Create Reminder
      </Button>
      <GenericFormPopup
        formFields={reminderFieldConfiguration}
        initialValues={reminderInitialValues}
        title="Create Reminder"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={createReminderMutation.isPending || isLoading}
        autocompleteData={{ leads: leadsOptions }}
      />
      <StatusSnackbar
        status={status}
        successMessage="Reminder created successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default ReminderPopup;
